import * as React from 'react';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { css, StyleSheet } from 'aphrodite/no-important';
import { getActiveEdition } from '../oldHeader/primaryHeader/Editions';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { useTheme } from '../../../../../theming/useTheme';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { getStyleBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { Link } from '../../../../partials/link/Link';
import { Directions, TriangleIcon } from '../TriangleIcon';
import { NavigationLink } from '../oldHeader/primaryHeader/PrimaryHeader';
import { EditionsIcon, EditionsIconDataProps } from '../header/EditionsIcon';

interface EditionsProps {
    editions: NavigationLink[];
    editionEndpoint: string;
    colorTypeHover: string;
    colorTypeNormal: string;
    colorTypeActive: string;
    editionsIcon: EditionsIconDataProps | null;
    iconColor: string;
    editionsIconHoverColor: string;
    dropdownIconColor: string;
    editionsMenuBackgroundColor: string;
}

const createStyles = ({
  colorTypeActive,
  colorTypeNormal,
  colorTypeHover,
  editionsMenuBackgroundColor,
}: EditionsProps, baseUnit: BaseUnit, editionsMenuToggle: boolean) => StyleSheet.create({
  button: {
    display: 'flex',
    color: colorTypeNormal,
    alignItems: 'center',
    cursor: 'pointer',
    ':hover': {
      color: colorTypeHover,
    },
    ':active': {
      color: colorTypeActive,
    },
  },
  link: {
    color: colorTypeNormal,
    ':hover': {
      color: colorTypeHover,
    },
    ':active': {
      color: colorTypeActive,
    },
  },
  ul: {
    backgroundColor: editionsMenuBackgroundColor,
    top: '100%',
    boxSizing: 'border-box',
    listStyle: 'none',
    margin: 0,
    display: editionsMenuToggle ? 'block' : 'none',
    zIndex: 100,
    whiteSpace: 'nowrap',
    columnCount: 1,
    height: 'fit-content',
    gap: 30,
    position: 'absolute',
    right: 0,
    boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.15)',
    maxWidth: 'max-content',
    padding: '1.5rem 1.5rem 1rem',
  },
  li: {
    display: 'block',
    whiteSpace: 'nowrap',
    fontSize: '0.875rem',
    lineHeight: '19px',
    textAlign: 'left',
    marginBottom: `${baseUnit}px`,
    [MEDIA_BREAKPOINTS.small]: {
      flex: '50%',
    },
  },
});

export const Editions: React.FunctionComponent<EditionsProps> = props => {
  const { baseUnit, fontSizesStyles } = useTheme();
  const {
    editions,
    editionEndpoint,
    editionsIcon,
    iconColor,
    editionsIconHoverColor,
    dropdownIconColor,
  } = props;
  const [editionsMenuToggle, setEditionsMenuToggle] = React.useState(false);
  const styles = createStyles(props, baseUnit, editionsMenuToggle);
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  const fontStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: TYPOGRAPHY_SIZE.TINY,
    fontSizeMedium: TYPOGRAPHY_SIZE.TINY,
    fontSizeLarge: TYPOGRAPHY_SIZE.TINY,
  });

  const scrollHandler = () => {
    if (editionsMenuToggle) {
      setEditionsMenuToggle(false);
    }
  };

  React.useEffect(() => {
    window.addEventListener('scroll', scrollHandler);

    return () => window.removeEventListener('scroll', scrollHandler);
  });

  const createEditionLink = (edition: NavigationLink, index: number) => (
    <li className={css(styles.li)} key={index}>
      <Link href={edition.link.href} className={css(styles.link, fontStyle)}>
        {edition.link.text}
      </Link>
    </li>
  );
  const EditionsMenu: React.FunctionComponent = () => (
    <ul className={css(styles.ul)}>
      {editions.map(createEditionLink)}
    </ul>
  );

  return (
    <React.Fragment>
      <button
        type="button"
        className={css(styles.button, fontStyle)}
        onClick={() => setEditionsMenuToggle(!editionsMenuToggle)}
      >
        {editionsIcon ? (
          <EditionsIcon
            {...editionsIcon}
            editionsIconNormalColor={iconColor}
            editionsIconHoverColor={editionsIconHoverColor}
          />
        ) : activeEdition.link.text}
        <TriangleIcon
          direction={editionsMenuToggle ? Directions.UP : Directions.DOWN}
          color={dropdownIconColor}
          hoverColor={iconColor}
        />
      </button>
      <EditionsMenu />
    </React.Fragment>
  );
};
